// The Web Audio half of the game's sound: one lazily-built graph, a handful
// of one-shot synths, and a five-layer ambience bed driven by tickAmbience.
// All of the numbers that decide how it sounds live in audioParams.ts, where
// they are pure and tested; this file is the side effects.
//
// Every public function is a safe no-op without a context: the unit tests run
// in happy-dom with no AudioContext, and makeKit's failure path has to leave
// the game silent, not broken.
import {
  DUCKS,
  DUCK_ATTACK_S,
  ambienceTargets,
  burgerNotes,
  crashLevelScale,
  duckIdle,
  duckPlan,
  grazeIntensity,
  hotDogNotes,
  ladderAdvance,
  ladderStart,
  landingDuck,
  landingImpact01,
  landingPeak,
  panForCourseX,
  retriggerOk,
  type AmbienceState,
  type DuckShape,
  type Ladder,
} from "./audioParams";
import { MUTED_KEY } from "./constants";

type Layer = { gain: GainNode; filter: BiquadFilterNode };

type AudioKit = {
  ctx: AudioContext;
  master: GainNode;
  sfx: GainNode;
  amb: GainNode;
  ambDuck: GainNode;
  ambLowpass: BiquadFilterNode;
  music: GainNode;
  musicDuck: GainNode;
  noise: AudioBuffer;
  ear: { gain: GainNode; band: BiquadFilterNode; lowpass: BiquadFilterNode };
  bed: Layer;
  slide: Layer;
  rumble: Layer;
  carve: Layer;
  whistle: Layer;
};

type Voice = { srcs: AudioScheduledSourceNode[]; gain: GainNode; startedAt: number };

const MASTER_GAIN = 0.8;
const MAX_VOICES = 16;

// The forest bed steps back when there is a score over it. Three dense noise
// layers and a full arrangement cannot both own the middle of the mix, and the
// bed is the one nobody is listening to on purpose.
const AMB_GAIN = 0.9;
const AMB_GAIN_WITH_MUSIC = 0.74;

let kit: AudioKit | null = null;
let ambTrim = false;
let ambLevel = 1;
let musicDuck = duckIdle();
let muted = loadMuted();
let ladder: Ladder = ladderStart();
let lastCrashAt = -Infinity;
let lastCrashKind = "";
let landPulseUntil = -Infinity;
let nextBirdAt = 0;
const lastPlayed = new Map<string, number>();
const voices: Voice[] = [];

function loadMuted(): boolean {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(MUTED_KEY) === "1";
  } catch {
    return false;
  }
}

function saveMuted(next: boolean): void {
  try {
    localStorage.setItem(MUTED_KEY, next ? "1" : "0");
  } catch {}
}

// One shared noise buffer feeds every bed layer and every burst. Two seconds
// so the loop period hides below the bandpasses, generated once so a crash
// never allocates; bursts pick a random start offset for variation.
function makeNoise(ctx: AudioContext): AudioBuffer {
  const buffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
}

function biquad(
  ctx: AudioContext,
  type: BiquadFilterType,
  frequency: number,
  q?: number,
  gainDb?: number,
): BiquadFilterNode {
  const f = ctx.createBiquadFilter();
  f.type = type;
  f.frequency.value = frequency;
  if (q !== undefined) f.Q.value = q;
  if (gainDb !== undefined) f.gain.value = gainDb;
  return f;
}

function chain(source: AudioNode, filters: BiquadFilterNode[], dest: AudioNode): void {
  let head: AudioNode = source;
  for (const f of filters) {
    head.connect(f);
    head = f;
  }
  head.connect(dest);
}

function layer(
  ctx: AudioContext,
  source: AudioNode,
  dest: AudioNode,
  filters: BiquadFilterNode[],
  gainValue: number,
): Layer {
  const gain = ctx.createGain();
  gain.gain.value = gainValue;
  chain(source, filters, gain);
  gain.connect(dest);
  return { gain, filter: filters[filters.length - 1]! };
}

// An LFO wired straight into an AudioParam: base value stays on the param,
// the oscillator's swing is added on top through a depth gain.
function lfo(ctx: AudioContext, hz: number, depth: number, param: AudioParam): void {
  const osc = ctx.createOscillator();
  osc.frequency.value = hz;
  const g = ctx.createGain();
  g.gain.value = depth;
  osc.connect(g);
  g.connect(param);
  osc.start();
}

// Noise lowpassed to sub-audio makes a random walk. The depth compensates for
// how little of white noise's energy survives below the cutoff.
function noiseWalk(
  ctx: AudioContext,
  noiseSrc: AudioNode,
  cutoffHz: number,
  depth: number,
  param: AudioParam,
): void {
  const lp = biquad(ctx, "lowpass", cutoffHz);
  const g = ctx.createGain();
  g.gain.value = depth;
  noiseSrc.connect(lp);
  lp.connect(g);
  g.connect(param);
}

function makeKit(): AudioKit | null {
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctx({ latencyHint: "interactive" });

    // master -> limiter -> out. The limiter is a safety lid, not an effect:
    // stacked synth one-shots would otherwise hard-clip the destination,
    // which phone speakers turn into crackle.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -18;
    limiter.knee.value = 6;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.25;
    limiter.connect(ctx.destination);

    const master = ctx.createGain();
    master.gain.value = muted ? 0 : MASTER_GAIN;
    master.connect(limiter);

    const sfx = ctx.createGain();
    sfx.gain.value = 0.7;
    sfx.connect(master);

    // The music bus: layers -> duck -> carve -> master. Two separate ways of
    // keeping the score behind the sound effects. The carve is a standing cut
    // where the rewards live, so no arrangement decision can put a harmonic on
    // top of a pickup; the duck is the dynamic half, dipped by every one-shot
    // below. Both exist so the mix never asks the master limiter to choose,
    // because the limiter would choose wrong -- it hears the music as the
    // loudest thing and would pull the sound effects down to make room for it.
    // The one number to move if the score sits too far forward or too far back
    // against the effects; everything below it is shape, not level.
    const music = ctx.createGain();
    music.gain.value = 0.27;
    const musicCarve = biquad(ctx, "peaking", 880, 1.1, -5);
    musicCarve.connect(music);
    music.connect(master);
    const musicDuck = ctx.createGain();
    musicDuck.gain.value = 1;
    musicDuck.connect(musicCarve);

    // The ambience bus: layers -> pink-ish tilt -> stun lowpass -> duck ->
    // master. Sustained flat noise is what fatigues; the shelves tilt the
    // whole bed warm, and the duck is how crashes buy their silence.
    const ambDuck = ctx.createGain();
    ambDuck.gain.value = 1;
    ambDuck.connect(master);
    const ambLowpass = biquad(ctx, "lowpass", 18000);
    ambLowpass.connect(ambDuck);
    const tiltHigh = biquad(ctx, "highshelf", 4000, undefined, -6);
    tiltHigh.connect(ambLowpass);
    const tiltLow = biquad(ctx, "lowshelf", 250, undefined, 3);
    tiltLow.connect(tiltHigh);
    const amb = ctx.createGain();
    amb.gain.value = ambTarget();
    amb.connect(tiltLow);

    const noise = makeNoise(ctx);
    const bedSrc = ctx.createBufferSource();
    bedSrc.buffer = noise;
    bedSrc.loop = true;
    bedSrc.start();

    // The bed layers, all parallel bands of the one noise source. Gains that
    // tickAmbience owns start at zero.
    const earLowpass = biquad(ctx, "lowpass", 250);
    const earBand = biquad(ctx, "bandpass", 480, 0.7);
    const earGain = ctx.createGain();
    earGain.gain.value = 0;
    chain(bedSrc, [earLowpass, earBand], earGain);
    earGain.connect(amb);

    const bed = layer(ctx, bedSrc, amb, [biquad(ctx, "bandpass", 300, 1)], 0.01);
    const hiss = layer(ctx, bedSrc, amb, [biquad(ctx, "bandpass", 3500, 2)], 0.01);
    const slide = layer(
      ctx,
      bedSrc,
      amb,
      [biquad(ctx, "highpass", 400), biquad(ctx, "bandpass", 1200, 0.9)],
      0,
    );
    const rumble = layer(ctx, bedSrc, amb, [biquad(ctx, "lowpass", 140)], 0);
    const carve = layer(ctx, bedSrc, amb, [biquad(ctx, "bandpass", 2800, 3)], 0);
    const whistle = layer(ctx, bedSrc, amb, [biquad(ctx, "bandpass", 1300, 12)], 0);

    // The forest gusts on two incommensurate sines plus a random walk, so the
    // bed never repeats and never sits still; the needle hiss rides the same
    // weather, appearing only on the peaks.
    lfo(ctx, 0.07, 0.006, bed.gain.gain);
    lfo(ctx, 0.11, 0.006, bed.gain.gain);
    noiseWalk(ctx, bedSrc, 0.4, 0.35, bed.gain.gain);
    lfo(ctx, 0.07, 0.005, hiss.gain.gain);
    lfo(ctx, 0.11, 0.005, hiss.gain.gain);

    // Snow is particulate. A 12Hz flutter on the slide gain is what separates
    // "surface under you" from "wind near you".
    noiseWalk(ctx, bedSrc, 12, 1.6, slide.gain.gain);

    return {
      ctx,
      master,
      sfx,
      amb,
      ambDuck,
      ambLowpass,
      music,
      musicDuck,
      noise,
      ear: { gain: earGain, band: earBand, lowpass: earLowpass },
      bed,
      slide,
      rumble,
      carve,
      whistle,
    };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Lifecycle.

export function unlockAudio(): void {
  if (!kit) {
    kit = makeKit();
    // A context born muted still renders; park it until the player unmutes.
    // Re-checked when the timer fires: the very click that unlocked the
    // graph can be the unmute, and suspending behind it would kill the
    // sound the player just asked for.
    if (kit && muted) {
      setTimeout(() => {
        if (muted) suspendAudio();
      }, 150);
    }
    // Hold the first bird back so it cannot land on top of the start cue.
    if (kit) nextBirdAt = kit.ctx.currentTime + 5;
  }
  if (!kit) return;
  // With the ringer switch flipped, iOS mutes web audio entirely unless the
  // session declares itself playback -- the game would look broken there.
  try {
    const session = (navigator as unknown as { audioSession?: { type: string } }).audioSession;
    if (session) session.type = "playback";
  } catch {}
  if (!muted && kit.ctx.state !== "running") void kit.ctx.resume();
}

// "suspended" is not the only stopped state: Safari parks the context as
// "interrupted" after a call or Siri, so the check is `!== "running"`.
export function resumeAudio(): void {
  if (kit && !muted && kit.ctx.state !== "running") void kit.ctx.resume();
}

export function suspendAudio(): void {
  if (kit && kit.ctx.state === "running") void kit.ctx.suspend();
}

// Mute suspends the context rather than just zeroing a gain: a muted running
// context still holds the phone's audio hardware awake and renders the bed
// forever, which is a battery bill for silence.
export function setMuted(next: boolean): void {
  muted = next;
  saveMuted(next);
  if (!kit) return;
  const { ctx, master } = kit;
  if (next) {
    master.gain.setTargetAtTime(0, ctx.currentTime, 0.015);
    setTimeout(() => {
      if (muted) suspendAudio();
    }, 120);
  } else {
    void ctx.resume();
    master.gain.setTargetAtTime(MASTER_GAIN, ctx.currentTime, 0.02);
    uiTick();
  }
}

export function isMuted(): boolean {
  return muted;
}

// For the QA harness: whether the graph was ever built, and what state the
// context is in. "none" before the first unlock; construction failures stay
// "none" forever, which is exactly what a smoke test wants to catch.
export function audioContextState(): string {
  return kit ? kit.ctx.state : "none";
}

// ---------------------------------------------------------------------------
// The music bus. music.ts owns the score; this file owns the graph it hangs
// from, so the two never import each other in a circle and every level in the
// mix is decided in one place.

/** Where music.ts attaches its layers, or null before the first unlock. */
export function musicBus(): { ctx: AudioContext; dest: AudioNode; noise: AudioBuffer } | null {
  if (!kit) return null;
  return { ctx: kit.ctx, dest: kit.musicDuck, noise: kit.noise };
}

// Told by music.ts whether there is a score to make room for. Recorded even
// with no context, because the preference is known before the first unlock and
// makeKit has to build the bed at the right level from the start.
export function setMusicPresent(on: boolean): void {
  ambTrim = on;
  if (!kit) return;
  kit.amb.gain.setTargetAtTime(ambTarget(), kit.ctx.currentTime, 0.3);
}

function ambTarget(): number {
  return (ambTrim ? AMB_GAIN_WITH_MUSIC : AMB_GAIN) * ambLevel;
}

export function setAmbienceLevel(level: number): void {
  ambLevel = Math.max(0, Math.min(1, level));
  if (!kit) return;
  kit.amb.gain.setTargetAtTime(ambTarget(), kit.ctx.currentTime, 0.05);
}

// Sidechain: every one-shot dips the score for its own duration. This is the
// direction that matters -- the music yields to the game, never the reverse.
// duckPlan owns which cue wins when two overlap; this applies its answer.
function duckMusic(shape: DuckShape): void {
  if (!kit) return;
  const t = kit.ctx.currentTime;
  const plan = duckPlan(musicDuck, t, shape);
  if (!plan) return;
  musicDuck = plan;
  const g = kit.musicDuck.gain;
  g.cancelScheduledValues(t);
  g.setValueAtTime(g.value, t);
  g.linearRampToValueAtTime(plan.floor, t + DUCK_ATTACK_S);
  g.setTargetAtTime(1, t + DUCK_ATTACK_S + shape.hold, shape.release);
}

// ---------------------------------------------------------------------------
// Voice plumbing for one-shots.

function pruneVoices(now: number): void {
  // Anything older than two seconds has decayed; the array stays tiny.
  let n = 0;
  for (const v of voices) if (now - v.startedAt < 2) voices[n++] = v;
  voices.length = n;
  if (voices.length < MAX_VOICES) return;
  // Steal the oldest with a fast fade -- an abrupt stop() at nonzero gain is
  // an audible click. Only a voice already sounding is fair game: anything
  // newer, or scheduled ahead of now, is part of the gesture being played
  // (a fanfare is a dozen scheduled voices at once), and cancelling its
  // envelope would silence it before it ever started.
  const i = voices.findIndex((v) => v.startedAt < now - 0.1);
  if (i < 0) return;
  const oldest = voices.splice(i, 1)[0]!;
  const g = oldest.gain.gain;
  g.cancelScheduledValues(now);
  g.setValueAtTime(g.value, now);
  g.linearRampToValueAtTime(0.0001, now + 0.005);
  for (const s of oldest.srcs) {
    try {
      s.stop(now + 0.01);
    } catch {}
  }
}

function gate(name: string, minGapS: number): boolean {
  if (!kit) return false;
  const now = kit.ctx.currentTime;
  if (!retriggerOk(lastPlayed.get(name) ?? -Infinity, now, minGapS)) return false;
  lastPlayed.set(name, now);
  return true;
}

type ShotOpts = {
  at?: number;
  attack?: number;
  peak: number;
  decay: number;
  pan?: number;
};

// Envelope, optional pan, voice-cap bookkeeping, and teardown for a one-shot.
// Callers start and stop their own sources (burst needs an offset argument);
// the envelope here is what guarantees the tail is silent by then.
function shot(srcs: AudioScheduledSourceNode[], last: AudioNode, opts: ShotOpts): number {
  if (!kit) return 0;
  const { ctx, sfx } = kit;
  const t = opts.at ?? ctx.currentTime;
  pruneVoices(ctx.currentTime);

  const g = ctx.createGain();
  g.gain.value = 0;
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(opts.peak, t + (opts.attack ?? 0.012));
  g.gain.exponentialRampToValueAtTime(0.0001, t + opts.decay);

  const nodes: AudioNode[] = [last, g];
  last.connect(g);
  let out: AudioNode = g;
  if (opts.pan !== undefined && typeof ctx.createStereoPanner === "function") {
    const p = ctx.createStereoPanner();
    p.pan.value = opts.pan;
    g.connect(p);
    out = p;
    nodes.push(p);
  }
  out.connect(sfx);

  voices.push({ srcs, gain: g, startedAt: t });
  const cleanup = [...srcs, ...nodes];
  srcs[srcs.length - 1]!.onended = () => {
    for (const n of cleanup) {
      try {
        n.disconnect();
      } catch {}
    }
  };
  return t;
}

// A noise burst cut from the shared buffer at a random offset -- variation
// without allocation.
function burst(filters: BiquadFilterNode[], opts: ShotOpts): void {
  if (!kit) return;
  const { ctx, noise } = kit;
  const src = ctx.createBufferSource();
  src.buffer = noise;
  // Looping lets a random offset land anywhere without a long tail running
  // off the end of the buffer; the envelope owns the actual duration.
  src.loop = true;
  let head: AudioNode = src;
  for (const f of filters) {
    head.connect(f);
    head = f;
  }
  const t = shot([src], head, opts);
  src.start(t, Math.random() * (noise.duration - 0.6));
  src.stop(t + opts.decay + 0.05);
}

function tone(
  type: OscillatorType,
  freq: number,
  opts: ShotOpts & { glideTo?: number; detuneCents?: number },
): void {
  if (!kit) return;
  const osc = kit.ctx.createOscillator();
  osc.type = type;
  const t = opts.at ?? kit.ctx.currentTime;
  osc.frequency.setValueAtTime(freq, t);
  if (opts.glideTo) osc.frequency.exponentialRampToValueAtTime(opts.glideTo, t + opts.decay * 0.8);
  if (opts.detuneCents) osc.detune.value = opts.detuneCents;
  shot([osc], osc, opts);
  osc.start(t);
  osc.stop(t + opts.decay + 0.05);
}

const cents = (range: number) => (Math.random() * 2 - 1) * range;
const vary = (v: number, spread: number) => v * (1 + (Math.random() * 2 - 1) * spread);

// ---------------------------------------------------------------------------
// One-shots.

export function sfxStart(): void {
  if (!kit || muted) return;
  ladder = ladderStart();
  const t = kit.ctx.currentTime;
  duckMusic(DUCKS.start);
  // Push-off scuff, then a two-note "go" a fifth apart.
  burst([biquad(kit.ctx, "lowpass", 900)], { peak: 0.2, decay: 0.24, attack: 0.02 });
  tone("triangle", 523.25, { at: t + 0.05, peak: 0.2, decay: 0.16, detuneCents: cents(12) });
  tone("triangle", 783.99, { at: t + 0.16, peak: 0.26, decay: 0.3, detuneCents: cents(12) });
}

export function sfxHit(kind: "tree" | "rock", atX: number): void {
  if (!kit || muted) return;
  if (!gate("hit", 0.08)) return;
  const { ctx, ambDuck } = kit;
  const t = ctx.currentTime;
  const pan = panForCourseX(atX);

  // Silence sells the impact: hard-duck the bed with the hitstop, recover as
  // the ride does.
  ambDuck.gain.cancelScheduledValues(t);
  ambDuck.gain.setValueAtTime(ambDuck.gain.value, t);
  ambDuck.gain.linearRampToValueAtTime(0.04, t + 0.03);
  ambDuck.gain.setTargetAtTime(1, t + 0.15, 0.25);
  duckMusic(DUCKS.crash);

  // Crashes cluster on bad runs; softening the repeats keeps the sound from
  // training players toward the mute button. A wider re-roll when the same
  // obstacle repeats keeps consecutive crashes from matching.
  const level = crashLevelScale(t - lastCrashAt) * vary(1, 0.12);
  const spread = kind === lastCrashKind ? 0.2 : 0.1;
  lastCrashAt = t;
  lastCrashKind = kind;
  ladder = ladderStart();

  // Three transient-aligned layers: crack, body, debris. Wood knocks lower
  // and rings longer than rock.
  const crackHz = vary(kind === "tree" ? 1100 : 2400, spread);
  const bodyHz = vary(kind === "tree" ? 80 : 110, spread);
  const debrisHz = vary(kind === "tree" ? 900 : 1400, spread);
  burst([biquad(ctx, "bandpass", crackHz, kind === "tree" ? 2 : 3)], {
    peak: 0.5 * level,
    decay: kind === "tree" ? 0.05 : 0.035,
    attack: 0.003,
    pan,
  });
  tone("sine", bodyHz, {
    peak: 0.5 * level,
    decay: 0.15,
    attack: 0.005,
    glideTo: bodyHz / 2,
    pan,
  });
  burst([biquad(ctx, "lowpass", debrisHz)], {
    at: t + 0.02,
    peak: 0.3 * level,
    decay: kind === "tree" ? 0.32 : 0.26,
    attack: 0.015,
    pan,
  });
}

export function sfxJump(atX: number): void {
  if (!kit || muted) return;
  if (!gate("jump", 0.05)) return;
  const pan = panForCourseX(atX);
  duckMusic(DUCKS.jump);
  const startF = 240 * (1 + cents(40) / 1200);
  burst([biquad(kit.ctx, "bandpass", 1500, 1)], { peak: 0.12, decay: 0.05, attack: 0.004, pan });
  tone("triangle", startF, { peak: 0.28, decay: 0.22, glideTo: startF * 2.15, pan });
}

export function sfxLand(fallSpeed: number, atX: number): void {
  if (!kit || muted) return;
  if (!gate("land", 0.06)) return;
  const impact = landingImpact01(fallSpeed);
  const pan = panForCourseX(atX);
  landPulseUntil = kit.ctx.currentTime + 0.2;
  duckMusic(landingDuck(impact));

  // Powder answers with a whump, deeper the harder you come down; a soft
  // touchdown gets a dry little stick instead.
  burst([biquad(kit.ctx, "lowpass", vary(180, 0.15))], {
    peak: landingPeak(impact),
    decay: 0.1 + 0.06 * impact,
    pan,
  });
  if (impact > 0.5) {
    tone("sine", 120, { peak: 0.15, decay: 0.12, glideTo: 70, pan });
  } else if (impact < 0.35) {
    tone("sine", 1800, { peak: 0.05, decay: 0.04, attack: 0.003, pan });
  }
}

export function sfxCollect(kind: "hotdog" | "burger", atX: number): void {
  if (!kit || muted) return;
  const now = kit.ctx.currentTime;
  // The ladder climbs on every pickup, gated or not: two foods in one substep
  // escalate instead of stacking the same chirp twice.
  const { play, next } = ladderAdvance(ladder, now);
  ladder = next;
  if (!gate("collect", 0.05)) return;

  duckMusic(DUCKS.collect);
  const pan = panForCourseX(atX);
  const timbre: OscillatorType = play % 2 === 0 ? "triangle" : "sine";
  if (kind === "hotdog") {
    hotDogNotes(play).forEach((f, i) => {
      tone(timbre, f, {
        at: now + i * 0.055,
        peak: 0.15,
        decay: 0.14,
        detuneCents: cents(20),
        pan,
      });
    });
    return;
  }
  // The burger is worth three hot dogs and sounds like it: a wider, longer
  // arpeggio with a sparkle an octave up.
  burgerNotes(play).forEach((f, i) => {
    const at = now + i * 0.07;
    tone(timbre, f, { at, peak: 0.19, decay: 0.24, detuneCents: cents(20), pan });
    tone("sine", f * 2, { at, peak: 0.07, decay: 0.34, detuneCents: 8, pan });
  });
}

export function sfxNearMiss(speedY: number, atX: number): void {
  if (!kit || muted) return;
  if (!gate("nearmiss", 0.15)) return;
  const intensity = grazeIntensity(speedY);
  const t = kit.ctx.currentTime;
  duckMusic(DUCKS.nearMiss);
  // Doppler falls as the tree passes.
  const f = biquad(kit.ctx, "bandpass", 900, 1.4);
  f.frequency.setValueAtTime(900, t);
  f.frequency.exponentialRampToValueAtTime(350, t + 0.18);
  burst([f], {
    peak: 0.12 + 0.12 * intensity,
    decay: 0.2,
    attack: 0.01,
    pan: panForCourseX(atX),
  });
}

export function sfxFinish(isBest: boolean): void {
  if (!kit || muted) return;
  const { ctx, ambDuck } = kit;
  const t = ctx.currentTime;

  // A beat of quiet before the chord, so the fanfare lands on silence.
  ambDuck.gain.cancelScheduledValues(t);
  ambDuck.gain.setValueAtTime(ambDuck.gain.value, t);
  ambDuck.gain.linearRampToValueAtTime(0.15, t + 0.04);
  ambDuck.gain.setTargetAtTime(1, t + 0.9, 0.5);
  duckMusic(DUCKS.finish);

  // The house arpeggio, octave-doubled. A new best does not replace it -- it
  // keeps climbing, so regulars hear "...and MORE" the moment it happens.
  const notes = isBest
    ? [392, 523.25, 659.25, 783.99, 1046.5, 1318.5]
    : [392, 523.25, 659.25, 783.99];
  notes.forEach((f, i) => {
    const at = t + 0.1 + i * 0.09;
    const held = i === notes.length - 1;
    tone("sine", f, { at, peak: 0.22, decay: held ? 1.3 : 0.35, attack: 0.02 });
    tone("triangle", f * 2.004, { at, peak: 0.08, decay: held ? 1.1 : 0.3, attack: 0.02 });
  });
  burst([biquad(ctx, "highpass", 5000)], {
    at: t + 0.1 + (notes.length - 1) * 0.09,
    peak: isBest ? 0.06 : 0.035,
    decay: isBest ? 1.5 : 0.8,
    attack: 0.05,
  });
}

export function sfxPosted(): void {
  if (!kit || muted) return;
  const t = kit.ctx.currentTime;
  duckMusic(DUCKS.posted);
  tone("sine", 659.25, { at: t, peak: 0.11, decay: 0.12 });
  tone("sine", 783.99, { at: t + 0.08, peak: 0.13, decay: 0.22 });
}

function uiTick(): void {
  if (!kit) return;
  tone("sine", 1200, { peak: 0.07, decay: 0.045, attack: 0.003 });
}

// ---------------------------------------------------------------------------
// Ambience.

const slew = {
  ear: 0.08,
  slideUp: 0.06,
  slideDown: 0.035,
  carveUp: 0.03,
  carveDown: 0.15,
  whistle: 0.1,
  bed: 0.1,
  stun: 0.04,
};

let lastSlideGain = 0;
let lastCarveGain = 0;

export function tickAmbience(state: AmbienceState): void {
  if (!kit || muted) return;
  const { ctx } = kit;
  const now = ctx.currentTime;
  const t = ambienceTargets(state);

  kit.ear.gain.gain.setTargetAtTime(t.ear.gain, now, slew.ear);
  kit.ear.lowpass.frequency.setTargetAtTime(t.ear.lowHz, now, slew.ear);
  kit.ear.band.frequency.setTargetAtTime(t.ear.bandHz, now, slew.ear);

  // Contact cuts fast -- losing the ground is near-instant -- and returns
  // with a brief overshoot on touchdown: the snow compresses, then the ride
  // settles.
  const pulse = now < landPulseUntil ? 1.4 : 1;
  const slideTarget = t.slide.gain * pulse;
  kit.slide.gain.gain.setTargetAtTime(
    slideTarget,
    now,
    slideTarget >= lastSlideGain ? slew.slideUp : slew.slideDown,
  );
  lastSlideGain = slideTarget;
  kit.slide.filter.frequency.setTargetAtTime(t.slide.bandHz, now, slew.ear);
  kit.rumble.gain.gain.setTargetAtTime(t.rumble, now, slew.slideUp);

  kit.carve.gain.gain.setTargetAtTime(
    t.carve.gain,
    now,
    t.carve.gain >= lastCarveGain ? slew.carveUp : slew.carveDown,
  );
  lastCarveGain = t.carve.gain;
  kit.carve.filter.frequency.setTargetAtTime(t.carve.bandHz, now, slew.carveUp);

  kit.whistle.gain.gain.setTargetAtTime(t.whistle, now, slew.whistle);
  kit.bed.gain.gain.setTargetAtTime(t.bed, now, slew.bed);
  kit.ambLowpass.frequency.setTargetAtTime(t.lowpassHz, now, slew.stun);

  // Distant birds, only when the hollow is quiet enough to hear them: the
  // title screen, the results card, a slow stretch of hill.
  if (state.idle || state.speed01 < 0.4) {
    if (now >= nextBirdAt) {
      nextBirdAt = now + 7 + Math.random() * 8;
      const f = 2900 + Math.random() * 500;
      const pan = (Math.random() * 2 - 1) * 0.3;
      tone("sine", f, { peak: 0.02, decay: 0.06, attack: 0.008, pan });
      tone("sine", f * 0.88, { at: now + 0.09, peak: 0.016, decay: 0.05, attack: 0.008, pan });
    }
  } else {
    nextBirdAt = Math.max(nextBirdAt, now + 4);
  }
}
